import React, { FC } from "react";
import { useEpisodeHook } from "./hooks/useEpisodeHook";
import { Button, Col, Row, Spin, Space, Dropdown, Checkbox } from "antd";
import type { MenuProps } from "antd";
import {
	ComponentTitle,
	PageTitleLastCol,
	SearchForm,
	SelectedCountTag,
} from "../custom-components";
import ChangeStatusModal from "./modals/ChangeStatusModal";
import { CustomTable } from "../series/StyledComponent";
import AddEpisodeForm from "./AddEpisodeForm";
import AddVideosMetaDataForm from "../videos/modals/AddVideosMetaDataForm";
import EpisodeFilterModal from "./modals/EpisodeFilterModal";
import { ClearFilterButton } from "../metadata";
import { CloseCircleOutlined, DownOutlined } from "@ant-design/icons";
import { TabComponentProps } from "../utils/common.interface";
import AddCaptionsForm from "../captions/modals/AddCaptionsForm";
import useScreenSizeHook from "../utils/useScreenSizeHook";
import { useNavigate } from "react-router-dom";
import CuePointsFormModal from "../cue-points/modals/CuePointsFormModal";
import ThumbnailFormModal from "../thumbnails/modals/ThumbnailFormModal";
import VideoPlayer from "../custom-components/VideoPlayer";
import { HomeTabKeyEnum } from "../utils";
import { CheckboxValueType } from "antd/es/checkbox/Group";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import CommonExportModal from "../videos/modals/export";
import BulkVideosTaggingModal from "../videos/modals/bulk-videos/BulkVideosTaggingModal";

export interface DistributionDataPropsTypes {
	distributionCheckedList: any[];
	distributionChange: (checkedValue: CheckboxValueType[]) => void;
	onDistributionCheckAllChange: (e: CheckboxChangeEvent) => void;
	checkAll: boolean;
	indeterminate: boolean;
}

const EpisodeComponent: FC<TabComponentProps> = () => {
	const navigate = useNavigate();
	const { height } = useScreenSizeHook();
	const pageURL = HomeTabKeyEnum.EPISODE;
	const {
		columnEpisode,
		isLoading,
		setIsEpisodeFormOpen,
		episodeData,
		isAddEpisdoeFormOpen,
		episodeEditId,
		form,
		seriesData,
		saveUpdateEpisodeData,
		cancelEpisodeFormHandler,
		metadataSelector,
		isAddNewMetadata,
		durationValue,
		setDurationValue,
		addNewInputHelper,
		videoEditId,
		setFormArray,
		formArray,
		setIsLoading,
		cancelVideoFormHandler,
		relatedMetadata,
		languages,
		tagsData,
		handleAddCustomTag,
		ref_id,
		isAddVideoFormOpen,
		saveUpdateVideoData,
		changeHandler,
		getFileteredData,
		filterEpisodeModalOpen,
		setFilterEpisodeModalOpen,
		filterModalCancelHandler,
		isFilteredData,
		clearFilter,
		handleTableChange,
		exportCSV,
		distributionPlatform,
		isExportModalOpen,
		setIsExportModalOpen,
		captionDataHandler,
		isOpenCaptionsForm,
		cancelCaptionFormHandler,
		captionEditId,
		metadataEditId,
		searchData,
		pagination,
		ratings,
		episodeFilterObj,
		episodeSortObj,
		saveCuePoint,
		handleCuePointCancel,
		cuePointEditId,
		isAddCuePointFormOpen,
		thumbnailEditId,
		isAddThumbnailFormOpen,
		saveEditThumbnail,
		handleThumbnailCancel,
		episodeRelatedDataTabHandler,
		episodeDataTabs,
		videoPlayerDetails,
		closeVideo,
		distributionCheckedList,
		distributionChange,
		onDistributionCheckAllChange,
		checkAll,
		indeterminate,
		isDuplicateCuePointFormOpen,
		cuepointFields,
		setCuepointFields,
		create5SecOffset,
		setCreate5SecOffset,
		videoLists,
		exportFormat,
		checkedKeys,
		colDropdownOptions,
		handleCheck,
		setExportFormat,
		activeDatesList,
		setActiveDatesList,
		selectedEpisodeData,
		clearEpisodeSelection,
		isChangeStatusModalOpen,
		setIsChangeStatusModalOpen,
		isBulkTaggingModalOpen,
		setIsBulkTaggingModalOpen,
		fetchEpisodeDataHandler,
		changeStatusModalCancelHandler,
		statusModalSubmitHandler,
	} = useEpisodeHook();

	const distributionProps = {
		distributionCheckedList,
		distributionChange,
		onDistributionCheckAllChange,
		checkAll,
		indeterminate,
	};

	const bulkActionItems: MenuProps["items"] = [
		{
			label: <span>Status</span>,
			key: "status",
			onClick: () => setIsChangeStatusModalOpen(true),
		},
		{
			label: <span>Tags</span>,
			key: "tags",
			onClick: () => setIsBulkTaggingModalOpen(true),
		},
	];

	const items: MenuProps["items"] = colDropdownOptions.map(col => ({
		key: col.key,
		label: (
			<div onClick={e => e.stopPropagation()}>
				<Checkbox
					checked={checkedKeys.includes(col.key)}
					onChange={e => handleCheck(col.key, e.target.checked)}
				>
					{col.label}
				</Checkbox>
			</div>
		),
	}));

	return (
		<Spin spinning={isLoading}>
			{isAddEpisdoeFormOpen ? (
				<AddEpisodeForm
					cancelEpisodeFormHandler={cancelEpisodeFormHandler}
					saveUpdateDataHandler={saveUpdateEpisodeData}
					seriesData={seriesData}
					form={form}
					tagsData={tagsData}
					episodeEditId={episodeEditId}
					ratings={ratings}
					episodeDataTabs={episodeDataTabs}
					episodeRelatedDataTabHandler={episodeRelatedDataTabHandler}
				/>
			) : (
				<>
					<Row justify="space-between" align="middle">
						<Col>
							<ComponentTitle>Episodes</ComponentTitle>
						</Col>
						<Col>
							{selectedEpisodeData.length > 0 && (
								<Space>
									<SelectedCountTag color="green">
										<span>{selectedEpisodeData.length} Selected</span>
										<span title="Clear Selection" onClick={clearEpisodeSelection} id="close-icon">
											X
										</span>
									</SelectedCountTag>
									<Dropdown menu={{ items: bulkActionItems }} trigger={["click"]}>
										<Button>
											Actions <DownOutlined />
										</Button>
									</Dropdown>
								</Space>
							)}
						</Col>
						<PageTitleLastCol>
							<Dropdown
								menu={{
									items,
									onClick: e => {
										e.domEvent.stopPropagation();
									},
								}}
								trigger={["click"]}
							>
								<Button>
									<Space>
										Columns to Display
										<DownOutlined />
									</Space>
								</Button>
							</Dropdown>
							&nbsp;
							<Button
								type="primary"
								onClick={() => {
									form.resetFields();
									setIsEpisodeFormOpen(true);
								}}
							>
								+ Add Episode
							</Button>
							&nbsp;
							<Button onClick={() => setIsExportModalOpen(true)}>Export</Button>
							&nbsp;
							<Button onClick={() => setFilterEpisodeModalOpen(true)}>Filters</Button>
							{isFilteredData && (
								<ClearFilterButton onClick={() => clearFilter()} icon={<CloseCircleOutlined />} />
							)}
							<SearchForm
								form={form}
								searchData={searchData}
								placeholder="Search by Ref ID or Metadata Title"
							/>
						</PageTitleLastCol>
					</Row>
					<CustomTable
						onChange={handleTableChange}
						columns={columnEpisode}
						dataSource={episodeData}
						scroll={{ x: columnEpisode.length > 8 ? columnEpisode.length * 150 : undefined, y: height - 265 }}
						pagination={pagination}
						onRow={(record: any) => {
							return {
								onClick: e => {
									if (e.metaKey || e.ctrlKey) {
										const newTab = window.open(pageURL + "/" + record.ref_id, "_blank");
										newTab?.focus();
									} else {
										sessionStorage.setItem("episodeFilter", JSON.stringify(episodeFilterObj));
										sessionStorage.setItem("episodeSort", JSON.stringify(episodeSortObj));
										navigate(`${pageURL}/${record.ref_id}`);
									}
								},
							};
						}}
					/>
				</>
			)}
			{isAddVideoFormOpen && (
				<AddVideosMetaDataForm
					metadataSelector={metadataSelector}
					isAddNewMetadata={isAddNewMetadata}
					durationValue={durationValue}
					setDurationValue={setDurationValue}
					addNewInputHelper={addNewInputHelper}
					videoEditId={videoEditId}
					setFormArray={setFormArray}
					formArray={formArray}
					form={form}
					isOpen={isAddVideoFormOpen}
					saveVideosData={saveUpdateVideoData}
					isLoading={isLoading}
					setIsLoading={setIsLoading}
					handleCancel={cancelVideoFormHandler}
					relatedMetadata={relatedMetadata}
					ref_id={ref_id}
					languages={languages}
					changeHandler={changeHandler}
					tagsData={tagsData}
					handleAddCustomTag={handleAddCustomTag}
					distributionPlatform={distributionPlatform}
					metadataEditId={metadataEditId}
					videosData={episodeDataTabs.data.videos}
					videoLists={videoLists}
					activeDatesList={activeDatesList}
					setActiveDatesList={setActiveDatesList}
					{...distributionProps}
				/>
			)}
			{filterEpisodeModalOpen && (
				<EpisodeFilterModal
					filterModalCancelHandler={filterModalCancelHandler}
					form={form}
					filterEpisodeModalOpen={filterEpisodeModalOpen}
					seriesData={seriesData}
					getFileteredData={getFileteredData}
					tagsData={tagsData}
					distributionPlatform={distributionPlatform}
				/>
			)}
			{isChangeStatusModalOpen && (
				<ChangeStatusModal
					cancelHandler={changeStatusModalCancelHandler}
					modalOpen={isChangeStatusModalOpen}
					form={form}
					submitHandler={statusModalSubmitHandler}
				/>
			)}
			{/* <ExportModal
				exportCSV={exportCSV}
				isExportModalOpen={isExportModalOpen}
				setIsExportModalOpen={setIsExportModalOpen}
				form={form}
			/> */}
			{isOpenCaptionsForm && (
				<AddCaptionsForm
					isLoading={isLoading}
					cancelHandler={cancelCaptionFormHandler}
					editCaptionId={captionEditId}
					form={form}
					isCaptionsModalOpen={isOpenCaptionsForm}
					languages={languages}
					saveUpdateCaptionsData={captionDataHandler}
					distributionPlatformData={distributionPlatform}
					{...distributionProps}
				/>
			)}
			{isAddCuePointFormOpen && (
				<CuePointsFormModal
					create5SecOffset={create5SecOffset}
					setCreate5SecOffset={setCreate5SecOffset}
					cuepointFields={cuepointFields}
					setCuepointFields={setCuepointFields}
					cuePointEditId={cuePointEditId}
					saveEditCuePointsData={saveCuePoint}
					isOpen={isAddCuePointFormOpen}
					handleCancel={handleCuePointCancel}
					isLoading={isLoading}
					form={form}
					distributionPlatformData={distributionPlatform}
					isDuplicateCuePointFormOpen={isDuplicateCuePointFormOpen}
					{...distributionProps}
				/>
			)}
			{isAddThumbnailFormOpen && (
				<ThumbnailFormModal
					handleAddCustomTag={handleAddCustomTag}
					thumbnailEditId={thumbnailEditId}
					isOpen={isAddThumbnailFormOpen}
					saveEditThumbnailData={saveEditThumbnail}
					handleCancel={handleThumbnailCancel}
					isLoading={isLoading}
					form={form}
					distributionPlatformData={distributionPlatform}
					tagsList={tagsData}
					{...distributionProps}
				/>
			)}
			<BulkVideosTaggingModal
				isBulkTaggingModalOpen={isBulkTaggingModalOpen}
				setIsBulkTaggingModalOpen={setIsBulkTaggingModalOpen}
				tagsList={tagsData}
				selectedData={selectedEpisodeData}
				getDataHandler={fetchEpisodeDataHandler}
				tagEntityType='episode'
			/>
			<CommonExportModal
				exportFormat={exportFormat}
				setExportFormat={setExportFormat}
				distributionPlatformData={distributionPlatform}
				isExportModalOpen={isExportModalOpen}
				setIsExportModalOpen={setIsExportModalOpen}
				form={form}
				exportCSV={exportCSV}
			/>
			{videoPlayerDetails && <VideoPlayer {...videoPlayerDetails} closeVideo={closeVideo} />}
		</Spin>
	);
};

export default EpisodeComponent;
